// The cardholder app's frame: the menu (by feature flag) down the left side, or along the bottom of a phone; the top
// bar with the signed-in name; the page.
import {
  firstName,
  Icon,
  initials,
  useBodyClass,
  useBrand,
  useFeatures,
  useSession,
  useTitle,
  type IconName,
} from '@qor/web-shared';
import { type ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

const WIDE_PAGES = new Set(['/onboarding/mdp', '/onboarding/zumrails']);

function NavLink({ to, icon, children }: { to: string; icon: IconName; children: ReactNode }) {
  const active = useLocation().pathname.toLowerCase() === to.toLowerCase();
  return (
    <Link
      className={`qcw-nav-link ${active ? 'qcw-nav-link-active' : ''}`}
      to={to}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name={icon} />
      <span>{children}</span>
    </Link>
  );
}

export function CustomerLayout() {
  const session = useSession();
  const user = session.data?.user ?? null;
  const brand = useBrand(user?.email);
  const features = useFeatures(user?.email);
  // the switched-off pages are left out of the menu once the flags are known (not shown, then taken away)
  const shows = (feature: string) => features.loaded && features.isEnabled(feature);
  const location = useLocation();
  const wide = WIDE_PAGES.has(location.pathname.toLowerCase());
  useBodyClass('qcw-body');
  // the menu depends on who is signed in: shown once that is known (no flash of the signed-out menu)
  if (session.isPending) return null;

  return (
    <>
      <a className="qcw-skip-link" href="#qcw-main">
        Skip to content
      </a>

      <aside className="qcw-sidebar">
        <Link className="qcw-sidebar-brand" to="/card" aria-label={`${brand.displayName} home`}>
          <img src={brand.logoUrl || '/images/qor-logo-mark.png'} alt={brand.displayName} />
        </Link>

        <nav className="qcw-nav" aria-label="Account navigation">
          <NavLink to="/card" icon="home">
            Overview
          </NavLink>
          {shows('Transactions') && (
            <NavLink to="/transactions" icon="transfer">
              Transactions
            </NavLink>
          )}
          {shows('CardSettings') && (
            <NavLink to="/card/settings" icon="card">
              Card Settings
            </NavLink>
          )}
          <NavLink to="/profile" icon="person">
            Profile
          </NavLink>
        </nav>

        <nav className="qcw-sidebar-footer" aria-label="Help">
          <NavLink to="/support" icon="support">
            Support
          </NavLink>
          <NavLink to="/logout" icon="signOut">
            Sign Out
          </NavLink>
          <div className="qcw-legal-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span aria-hidden="true">&middot;</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </nav>
      </aside>

      <div className="qcw-shell">
        <header className="qcw-topbar">
          <span className="qcw-topbar-spacer"></span>
          {user ? (
            <Link className="qcw-account-menu" to="/profile" aria-label="Your profile">
              <span className="qcw-avatar" aria-hidden="true">
                {initials(user.displayName)}
              </span>
              <span className="qcw-account-name">{firstName(user.displayName, user.displayName)}</span>
            </Link>
          ) : (
            <Link className="qcw-account-menu" to="/login" aria-label="Sign in">
              <span className="qcw-account-name">Sign in</span>
            </Link>
          )}
        </header>

        <main className={`qcw-content ${wide ? 'qcw-content-wide' : ''}`} id="qcw-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </>
  );
}

/** The page <title> with the brand, inside the layout. */
export function usePageTitle(title: string) {
  const session = useSession();
  const brand = useBrand(session.data?.user?.email);
  useTitle(title, brand);
}
